import { switchingRoleOf } from "../switchingRoleOf";
import { wiringBasisOf } from "../wiringBasisOf";
import { cableTypesOfCircuit } from "../circuitLegs";
import type { LegKind } from "../circuitLegs";
import { FEEDER_CABLE_SUGGESTION } from "../feederCircuits";
import { countLiveCircuits } from "../countLiveCircuits";
import type { CircuitPathResult } from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import type { WiringBasis } from "../wiringBasisOf";
import type { DescribeLiveTakeoff } from "./types";

const m = (value: number, digits = 1): string => `${value.toFixed(digits)} m`;

const countNames = (names: string[]): string => {
  const counts = new Map<string, number>();
  names.forEach((name) => counts.set(name, (counts.get(name) ?? 0) + 1));
  return [...counts.entries()].map(([name, count]) => `${count}× ${name}`).join(", ");
};

const describeWiring = (basis: WiringBasis, board: string): string => {
  const units = basis.units.map(
    (unit) =>
      `${[board, unit.via, unit.control].filter(Boolean).join(" → ")}` +
      (unit.lights ? ` → ${unit.lights} light(s) chained` : "") +
      (unit.loads.length ? `; ${unit.loads.join(", ")} on its own run from the switch` : ""),
  );
  const direct = basis.directCount ? [`${basis.directCount} device(s) straight from ${board} over the tray`] : [];
  return [...(units.length ? ["switch-first"] : []), ...units, ...direct].join("; ");
};

export const describeLiveTakeoff: DescribeLiveTakeoff = ({
  edit,
  items,
  circuitName,
  trayTypes,
}) => {
  const geometry = edit.derivedGeometry;
  if (!geometry) return "";
  const deviceTypes = geometry.deviceTypeByElementId;
  const nameOf = (path: CircuitPathResult) =>
    (path.elementId ? deviceTypes?.[path.elementId]?.name : undefined) ?? path.destinationName;
  const boardName = (label: string | null | undefined) =>
    (label && (edit.sourceNames[label] ?? geometry.sources.find((s) => s.label === label)?.name)) || label || "?";
  const itemOf = new Map(items.filter((item) => item.circuit).map((item) => [item.circuit as string, item]));

  const mainLabel = edit.mainBoard.kind === "none" ? null : edit.mainBoard.label;
  const boards = geometry.sources.map(
    (source) =>
      `${boardName(source.label)} (${source.label}${source.label === mainLabel ? ", main board" : ""}, cable exit at ${m(edit.heights.boardM(source.label), 2)})`,
  );
  const { levels } = edit.heights;
  const trays = trayTypes.map((type) => `${type || "untyped tray"} ${m(edit.heights.trayM(type), 2)}`);

  const LEG_NAMES: Record<LegKind, string> = {
    feed: "feed to the switch",
    traveler: "between the two-way switches",
    switched: "switch to lights",
    load: "switch to the Lüfter",
  };
  const describeCable = (circuitId: string, circuitType: string): string => {
    const legs = edit.legsByCircuit[circuitId];
    const legTypes = edit.cableTypeByLegByCircuit[circuitId];
    const shares = legs && legTypes ? cableTypesOfCircuit({ legs, circuitType, legTypes }) : [];
    if (shares.length < 2) return `cable ${circuitType}`;
    return `cables ${shares
      .map((share) => `${share.cableType} ${m(share.totalM)} (${share.kinds.map((kind) => LEG_NAMES[kind]).join(", ")})`)
      .join(" + ")}`;
  };

  const setupLines = [
    `BOARDS: ${boards.join("; ")}`,
    `HEIGHTS: floor installation ${m(levels.floorM, 2)}, switches/sockets ${m(levels.controlsM, 2)}, luminaire outlets ${m(levels.luminaireM, 2)}, ceiling void ${m(levels.ceilingVoidM, 2)}`,
    `TRAYS: ${trays.join("; ") || "none"}`,
  ];
  if (!edit.isSetupDone) {
    return [
      "SETUP IN PROGRESS: boards, heights, trays and walls are still being confirmed with the estimator. Nothing has been calculated yet: there are no lengths and no total, and none may be quoted.",
      ...setupLines,
    ].join("\n");
  }

  const routed = Object.entries(geometry.byCircuit).filter(([, state]) => state.paths.length);
  const circuitCounts = countLiveCircuits({ edit });
  const circuits = routed
    .map(([circuitId, state]) => {
      const feeder = edit.feederCircuits[circuitId];
      if (feeder) {
        const confirmed = edit.cableTypeByCircuit[circuitId];
        return [
          `FEEDER ${boardName(feeder.fromLabel)} → ${boardName(feeder.toLabel)}`,
          `cable ${confirmed ?? `${FEEDER_CABLE_SUGGESTION} (suggested, not confirmed by the estimator)`}`,
          `horizontal ${m(edit.billedByCircuit[circuitId] ?? 0)}, vertical ${m(edit.verticalByCircuit[circuitId]?.verticalM ?? 0)}, total ${m(edit.derivedLengthsByCircuit[circuitId] ?? 0)}`,
        ].join(" | ");
      }
      const board = edit.feedingBoardByCircuit[circuitId] ?? null;
      const item = itemOf.get(circuitId);
      const tray = geometry.trayByCircuit?.[circuitId];
      const onTray = tray && tray.billedM > 0 ? ` | ${Math.round((tray.onTrayM / tray.billedM) * 100)}% on tray` : "";
      const wiring = wiringBasisOf({
        paths: state.paths,
        roleOf: (path) =>
          switchingRoleOf({
            deviceType: path.elementId ? deviceTypes?.[path.elementId] : undefined,
            name: path.destinationName,
          }),
        nameOf,
      });
      const facts = [
        `CIRCUIT ${circuitId}`,
        `name "${circuitName(circuitId)}"`,
        `board ${boardName(board)}`,
        describeCable(circuitId, edit.cableTypeByCircuit[circuitId] ?? item?.cable_type ?? "?"),
        `${state.paths.length} device(s): ${countNames(state.paths.map(nameOf))}`,
        `horizontal ${m(edit.billedByCircuit[circuitId] ?? 0)}, vertical ${m(edit.verticalByCircuit[circuitId]?.verticalM ?? 0)}, total ${m(edit.derivedLengthsByCircuit[circuitId] ?? 0)}${onTray}`,
        `wiring: ${describeWiring(wiring, boardName(board))}`,
      ];
      if (edit.addedCircuits[circuitId]) facts.push("created by the estimator in the app");
      if (edit.circuitsNeedingRoute.includes(circuitId)) facts.push("edited since it was last routed");
      return facts.join(" | ");
    });

  return [
    // The panel counts feeders apart ("Zuleitungen"), so Karl must too or his circuit count disagrees with it.
    `TOTAL ${m(edit.derivedTotalM)} over ${circuitCounts.circuits} circuits plus ${circuitCounts.feeders} feeder(s) (in German: Zuleitungen, never Einspeisungen — that word is the boards)`,
    ...setupLines,
    "FEEDERS: a FEEDER line is the cable that supplies a board from the board upstream of it, routed and measured like a circuit; boards fed from another floor have none on this plan.",
    "RULES: a circuit's length = horizontal (routed, off-tray metres carry the no-tray surcharge) + vertical (board to tray, tray changes, drops to each device). A lighting circuit with a switch or motion sensor is wired switch-first: board → switch over the tray, then the lights chained nearest-first, a Lichtband along its row, a Lüfter on its own run from the switch.",
    ...circuits,
  ].join("\n");
};
